import type { FunnelParams } from '@/model/types';
import { API_BASE_URL, apiBase } from './apiConfig';

/**
 * The admin console's client.
 *
 * Deliberately NOT part of `PersistencePort`, for the same reason `historyClient`
 * is not: none of this exists without the service. localStorage has no notion of
 * a scenario registry, so folding it into the port would force the local adapter
 * to implement methods that can only ever return nothing. A separate client that
 * is simply absent when there is no API says the true thing, and lets the console
 * say "the admin console needs the scenario service" rather than show an empty
 * list that looks like a fresh install.
 *
 * It also does NOT reuse `apiConfig.writeHeaders()`. Those headers carry
 * `X-Session-Id`, and an admin request must never carry one — leaning on
 * `getSessionId()` returning null in admin mode would make that a property of a
 * flag in another file rather than of this one.
 *
 * Every outcome is a value rather than an exception, matching `LoadResult`: "the
 * service refused you" and "I could not reach the service" are different answers
 * that send someone to look in completely different places, and a thrown Error
 * collapses exactly the distinction this app is built around.
 */

/** What the service mints in exchange for the typed code. */
export type AdminSession = {
  token: string;
  /** ISO 8601. The console locks itself when this passes. */
  expiresAt: string;
};

/** One row of `GET /api/admin/scenarios`. Mirrors the handler's wire shape. */
export type AdminScenario = {
  accessCode: string;
  version: number;
  revisions: number;
  updatedAt: string;
  /** Soft-deleted codes are still listed: the revision log outlives them. */
  deletedAt: string | null;
};

export type UnlockResult =
  | { status: 'unlocked'; session: AdminSession }
  /** The service answered, and the answer was no. A definite verdict. */
  | { status: 'rejected' }
  /** Too many attempts. Distinct from a refusal so the copy can say to wait. */
  | { status: 'throttled' }
  /** The service could not be reached. Not a verdict on the code. */
  | { status: 'unavailable' };

export type ListResult =
  | { status: 'ok'; scenarios: AdminScenario[] }
  /** Token expired or revoked. The console must lock, not retry. */
  | { status: 'unauthorized' }
  | { status: 'unavailable' };

export type CreateResult =
  | { status: 'created'; accessCode: string }
  /** That code already exists. Not a failure — an answer. */
  | { status: 'conflict' }
  | { status: 'unauthorized' }
  | { status: 'unavailable' };

export type AdminClient = {
  unlock(code: string): Promise<UnlockResult>;
  listScenarios(token: string): Promise<ListResult>;
  createScenario(token: string, accessCode: string, params: FunnelParams): Promise<CreateResult>;
};

export function createAdminClient(baseUrl: string): AdminClient {
  const base = baseUrl.replace(/\/+$/, '');
  const admin = (path: string) => `${base}/api/admin${path}`;

  /* The token is a parameter on every call rather than client state, so exactly
     one place in the app holds it — AdminConsole's useState — and it dies with
     that component. It also keeps this module trivially testable. */
  const authed = (token: string): Record<string, string> => ({
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  });

  return {
    async unlock(code) {
      let response: Response;
      try {
        response = await fetch(admin('/session'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({ code }),
        });
      } catch {
        /* A throw is transport — DNS, CORS, a dead socket — and never a verdict
           on the code. Reporting it as a rejection would send an admin hunting
           for a typo in a code that is perfectly correct. */
        return { status: 'unavailable' };
      }

      if (response.status === 429) return { status: 'throttled' };
      if (response.status === 400 || response.status === 401 || response.status === 403) {
        return { status: 'rejected' };
      }
      if (!response.ok) return { status: 'unavailable' };

      const body = (await response.json().catch(() => null)) as Partial<AdminSession> | null;
      /* A 200 with no token is not a successful unlock. Failing closed here beats
         entering the console with an empty Bearer and 401ing on every call. */
      if (!body?.token) return { status: 'unavailable' };
      return {
        status: 'unlocked',
        session: { token: body.token, expiresAt: body.expiresAt ?? '' },
      };
    },

    async listScenarios(token) {
      let response: Response;
      try {
        response = await fetch(admin('/scenarios'), {
          headers: authed(token),
          credentials: 'omit',
        });
      } catch {
        return { status: 'unavailable' };
      }
      if (response.status === 401 || response.status === 403) return { status: 'unauthorized' };
      if (!response.ok) return { status: 'unavailable' };

      const body = (await response.json().catch(() => null)) as
        | { scenarios?: AdminScenario[] }
        | null;
      /* An empty array is a real answer here, unlike in the load path: the
         service confirmed there is nothing provisioned. */
      return { status: 'ok', scenarios: body?.scenarios ?? [] };
    },

    async createScenario(token, accessCode, params) {
      let response: Response;
      try {
        response = await fetch(admin('/scenarios'), {
          method: 'POST',
          headers: { ...authed(token), 'Content-Type': 'application/json' },
          credentials: 'omit',
          /* The defaults travel from here rather than living on the server: the
             service keeps no runtime copy of the model, which is the invariant
             its drift guard protects. */
          body: JSON.stringify({ accessCode, params }),
        });
      } catch {
        return { status: 'unavailable' };
      }
      if (response.status === 401 || response.status === 403) return { status: 'unauthorized' };
      if (response.status === 409) return { status: 'conflict' };
      if (!response.ok) return { status: 'unavailable' };

      return { status: 'created', accessCode };
    },
  };
}

/**
 * Null when no API is configured, which the console reads as "there is nothing
 * to administer" rather than "no scenarios exist".
 */
export const adminClient: AdminClient | null = API_BASE_URL
  ? createAdminClient(apiBase())
  : null;
